import { action, makeObservable, observable } from 'mobx';
import { FormField } from './formField';
import { RootStore } from './root';
import { ROUTES } from '../routes';
import { DEVICE_LOGIN_METHODS, WIX_HOME_URL } from '../utils/constants';
import {
  deviceLoginPageLoadedSrc113Evid352,
  clickInDeviceLoginPageSrc113Evid353,
} from '@wix/bi-logger-pos-data/v2';

export class PosDeviceLoginStore {
  private readonly rootStore: RootStore;
  private readonly authHandlers: {
    SIGNUP: () => void;
    CONNECTION_SUCCESSFUL: () => void;
  };
  public code: FormField;
  private VERIFY_CODE_INTERACTION_NAME = 'verify-code';
  public codeLength = 8;
  private urlParams: URLSearchParams = new URLSearchParams(
    window.location.search,
  );
  private isDebug: boolean =
    this.urlParams.get('debugCase') === 'posDeviceLogin';

  get contentKey(): string {
    return 'posDevice.login.connect.currently.logged.in';
  }

  get currentLoggedInEmail(): string {
    const userDataLoggedInEmail =
      this.rootStore.userDataStore.currentLoggedInEmail;
    if (userDataLoggedInEmail) {
      return userDataLoggedInEmail;
    } else {
      return this.isDebug ? this.urlParams.get('email') ?? '' : '';
    }
  }

  reportPageOpened(): void {
    void this.rootStore.biLogger.report(deviceLoginPageLoadedSrc113Evid352({}));
  }

  reportButtonClicked(cta: string): void {
    void this.rootStore.biLogger.report(
      clickInDeviceLoginPageSrc113Evid353({ cta }),
    );
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const {
      navigationStore: { curryNavigate: navigate },
    } = rootStore;
    makeObservable(this, {
      submit: action.bound,
      code: observable,
    });

    this.code = new FormField('');

    this.authHandlers = {
      [DEVICE_LOGIN_METHODS.SIGNUP]: navigate(ROUTES.SIGNUP_PASSWORD),
      [DEVICE_LOGIN_METHODS.CONNECTION_SUCCESSFUL]: navigate(
        ROUTES.POS_DEVICE_CONNECTION_SUCCESSFUL,
      ),
    };
  }

  public enableAccess(): boolean {
    return !!this.rootStore.userDataStore.currentLoggedInEmail || this.isDebug;
  }

  public redirectToWix() {
    return this.rootStore.navigationStore.redirect(WIX_HOME_URL);
  }

  public async submit() {
    if (!this.code.isValid) {
      return;
    }
    if (this.code.value.length < 8 || this.code.value.length > 8) {
      this.code.addError(
        this.rootStore.i18n.t(
          'posDevice.login.code.input.code.length.errorInfo',
        ),
      );
      return;
    }

    this.rootStore.fedopsLogger.interactionStarted(
      this.VERIFY_CODE_INTERACTION_NAME,
    );
    const code = this.code.value;
    const { apiStore } = this.rootStore;
    try {
      const verifyCode = await apiStore.deviceVerifyCode(code);

      if (verifyCode === 200) {
        this.authHandlers[DEVICE_LOGIN_METHODS.CONNECTION_SUCCESSFUL]();
      }
      this.rootStore.fedopsLogger.interactionEnded(
        this.VERIFY_CODE_INTERACTION_NAME,
      );
    } catch (error: any) {
      this.rootStore.fedopsLogger.interactionEnded(
        this.VERIFY_CODE_INTERACTION_NAME,
      );
      this.code.addError(
        this.rootStore.i18n.t('posDevice.login.code.input.incorrect.errorInfo'),
      );
    }
  }
}
